// services/giftCardService.js
import { ref, get, update, set, push } from 'firebase/database';
import { database } from '../../firebase';

/**
 * Service for handling gift card operations
 */
const giftCardService = {
  /**
   * Find a gift card by its code
   * @param {string} code - Gift card code
   * @returns {Promise<Object|null>} Gift card data or null if not found
   */
  findByCode: async (code) => {
    if (!code) return null;
    
    // Clean up code for verification
    const cleanCode = code.replace(/-/g, '').toUpperCase();
    
    try {
      // Query the database for the gift card
      const giftCardsRef = ref(database, 'giftCards');
      const snapshot = await get(giftCardsRef);
      
      if (!snapshot.exists()) {
        return null;
      }
      
      // Find the gift card with matching code
      let foundGiftCard = null;
      let giftCardId = null;
      
      snapshot.forEach((childSnapshot) => {
        const card = childSnapshot.val();
        if (card.code && card.code.replace(/-/g, '').toUpperCase() === cleanCode) {
          foundGiftCard = { ...card, id: childSnapshot.key };
          giftCardId = childSnapshot.key;
        }
      });
      
      if (!foundGiftCard) {
        return null;
      }
      
      // Check if gift card is expired
      const expirationDate = new Date(foundGiftCard.expiresAt);
      if (expirationDate < new Date()) {
        return { ...foundGiftCard, error: 'expired' };
      }
      
      // Check if gift card has balance
      if (foundGiftCard.status === 'redeemed' || foundGiftCard.balance <= 0) {
        return { ...foundGiftCard, error: 'no_balance' };
      }
      
      // Gift card is valid
      return foundGiftCard;
      
    } catch (error) {
      console.error('Error finding gift card:', error);
      throw error;
    }
  },
  
  /**
   * Create a new gift card
   * @param {Object} giftCardData - Gift card data
   * @param {string} [userId] - User ID if available
   * @returns {Promise<Object>} Created gift card
   */
  create: async (giftCardData, userId = null) => {
    try {
      // Generate a unique gift card code if not provided
      const code = giftCardData.code || giftCardService.generateCode();
      
      // Ensure amount is a number
      const amount = parseFloat(giftCardData.amount);
      if (isNaN(amount)) {
        throw new Error('Invalid gift card amount');
      }
      
      // Create the gift card data
      const giftCard = {
        ...giftCardData,
        code,
        status: 'active',
        balance: amount,
        amount: amount, // Ensure amount is stored consistently
        createdAt: giftCardData.createdAt || new Date().toISOString(),
        expiresAt: giftCardData.expiresAt || new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString(), // 1 year from now
        transactions: giftCardData.transactions || [{
          type: 'creation',
          amount: amount,
          date: new Date().toISOString()
        }]
      };
      
      console.log("Creating gift card with data:", giftCard);
      
      // Create reference in giftCards collection
      const giftCardRef = push(ref(database, 'giftCards'));
      await set(giftCardRef, giftCard);
      const giftCardId = giftCardRef.key;
      
      console.log("Gift card created with ID:", giftCardId);
      
      // If user is logged in, also store in their account
      if (userId) {
        console.log("Adding gift card to user account:", userId);
        
        const userGiftCardData = {
          id: giftCardId,
          type: 'purchased',
          recipientName: giftCard.recipientName || '',
          amount: amount,
          createdAt: giftCard.createdAt
        };
        
        await set(ref(database, `users/${userId}/giftCards/${giftCardId}`), userGiftCardData);
        console.log("Gift card added to user account successfully");
      }
      
      return {
        ...giftCard,
        id: giftCardId
      };
      
    } catch (error) {
      console.error('Error creating gift card:', error);
      throw error;
    }
  },
  
  /**
   * Apply gift card to a booking
   * @param {string} giftCardId - Gift card ID
   * @param {string} bookingId - Booking ID
   * @param {number} amount - Amount to apply
   * @returns {Promise<Object>} Result of the operation
   */
  applyToBooking: async (giftCardId, bookingId, amount) => {
    try {
      // Get the gift card
      const giftCardRef = ref(database, `giftCards/${giftCardId}`);
      const snapshot = await get(giftCardRef);
      
      if (!snapshot.exists()) {
        throw new Error('Gift card not found');
      }
      
      const giftCard = snapshot.val();
      
      // Check if gift card has enough balance
      if (giftCard.balance < amount) {
        throw new Error('Insufficient gift card balance');
      }
      
      // Calculate new balance
      const newBalance = Math.max(0, giftCard.balance - amount);
      
      // Update gift card with new balance and add transaction
      const updates = {
        balance: newBalance,
        status: newBalance > 0 ? 'active' : 'redeemed'
      };
      
      // Prepare transaction record
      const newTransaction = {
        type: 'redemption',
        amount,
        bookingId,
        date: new Date().toISOString()
      };
      
      // Add to transactions array if it exists, otherwise create it
      if (giftCard.transactions && Array.isArray(giftCard.transactions)) {
        updates.transactions = [...giftCard.transactions, newTransaction];
      } else {
        updates.transactions = [newTransaction];
      }
      
      // Update the gift card
      await update(giftCardRef, updates);
      
      return {
        id: giftCardId,
        amountApplied: amount,
        remainingBalance: newBalance
      };
      
    } catch (error) {
      console.error('Error applying gift card to booking:', error);
      throw error;
    }
  },
  
  /**
   * Get all gift cards for a user
   * @param {string} userId - User ID
   * @returns {Promise<Array>} Array of gift cards
   */
  getUserGiftCards: async (userId) => {
    try {
      const userGiftCardsRef = ref(database, `users/${userId}/giftCards`);
      const snapshot = await get(userGiftCardsRef);
      
      if (!snapshot.exists()) {
        return [];
      }
      
      // Get full gift card details
      const giftCards = [];
      const promises = [];
      
      snapshot.forEach((childSnapshot) => {
        const userGiftCard = childSnapshot.val();
        
        // Skip if no ID
        if (!userGiftCard.id) {
          console.warn('Gift card without ID found:', userGiftCard);
          return;
        }
        
        // Get the full gift card details
        const promise = get(ref(database, `giftCards/${userGiftCard.id}`))
          .then((giftCardSnapshot) => {
            if (giftCardSnapshot.exists()) {
              const giftCard = giftCardSnapshot.val();
              giftCards.push({
                ...giftCard,
                id: userGiftCard.id,
                type: userGiftCard.type || 'purchased' // 'purchased' or 'received'
              });
            } else {
              console.warn(`Gift card ${userGiftCard.id} not found in main collection`);
            }
          })
          .catch(error => {
            console.error(`Error fetching gift card ${userGiftCard.id}:`, error);
          });
        
        promises.push(promise);
      });
      
      await Promise.all(promises);
      
      console.log(`Retrieved ${giftCards.length} gift cards for user ${userId}`);
      
      // Sort by created date, newest first
      return giftCards.sort((a, b) => {
        return new Date(b.createdAt) - new Date(a.createdAt);
      });
      
    } catch (error) {
      console.error('Error getting user gift cards:', error);
      throw error;
    }
  },
  
  /**
   * Generate a unique gift card code
   * @returns {string} A formatted gift card code
   */
  generateCode: () => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
    let result = '';
    for (let i = 0; i < 16; i++) {
      if (i > 0 && i % 4 === 0) result += '-';
      result += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return result;
  },
  
  /**
   * Format gift card code with dashes for display
   * @param {string} code - Gift card code
   * @returns {string} Formatted code
   */
  formatCode: (code) => {
    if (!code) return '';
    
    // Clean up any existing formatting
    const cleanCode = code.replace(/-/g, '').toUpperCase();
    
    // Format with dashes every 4 characters
    let formattedCode = '';
    for (let i = 0; i < cleanCode.length; i++) {
      if (i > 0 && i % 4 === 0) formattedCode += '-';
      formattedCode += cleanCode[i];
    }
    
    return formattedCode;
  }
};

export default giftCardService;